import siteConfig, { formatCurrency } from "../config/siteConfig";

const { brandName, contact, shipping, returns, support } = siteConfig;

const contentPages = {
  about: {
    slug: "about",
    path: "/about",
    eyebrow: "Our story",
    title: "A living map of flavor",
    intro: `${brandName} began with a simple belief: a well-sourced spice should tell you where it came from, when it was packed, and why it tastes remarkable.`,
    heroImage: "/images/generated/about-hero.png",
    sections: [
      {
        id: "source-with-context",
        title: "Source with context",
        body: [
          "We look for growers and mills who can speak clearly about place, harvest, and handling. Origin is not a decorative label; it is part of flavor.",
          "Some ingredients come from one farm or region, while our house blends are composed in small batches for madezuno.",
        ],
      },
      {
        id: "freshness-first",
        title: "Freshness before abundance",
        body: [
          "We buy in focused lots, grind frequently, and keep the assortment intentionally edited. That means fewer sleepy jars and more vivid cooking.",
          "Every product page includes practical flavor notes and kitchen ideas, because a special ingredient should be easy to use on an ordinary Tuesday.",
        ],
      },
      {
        id: "responsible-pantry",
        title: "A more thoughtful pantry",
        body: [
          "Our reusable glass jars are designed for the counter and our lightweight refill pouches use less material in transit. We continue to reduce excess packaging as better options become available.",
        ],
        highlights: ["Small-batch milling", "Refillable pantry jars", "Direct, useful sourcing notes"],
      },
    ],
  },
  shipping: {
    slug: "shipping",
    path: "/shipping",
    eyebrow: "Customer care",
    title: "Shipping",
    intro: `Standard shipping is ${formatCurrency(shipping.standardRate)} to supported delivery destinations.`,
    sections: [
      {
        id: "rates",
        title: "Rates and timing",
        items: [
          {
            label: shipping.standardLabel,
            value: `${formatCurrency(shipping.standardRate)} · ${shipping.estimatedDelivery}`,
          },
          {
            label: shipping.expressLabel,
            value: `${formatCurrency(shipping.expressRate)} · ${shipping.expressDelivery}`,
          },
        ],
        body: [
          "Delivery estimates begin once an order leaves our pantry. Weekends, holidays, weather, and carrier disruptions can add time.",
        ],
      },
      {
        id: "packing",
        title: "How we pack",
        body: [
          "Most orders are packed within two business days. Glass jars are wrapped with curbside-recyclable paper cushioning whenever local recycling rules allow.",
          "If an item in your order is temporarily unavailable, we will contact you before making a substitution or splitting the shipment.",
        ],
      },
      {
        id: "international",
        title: "International orders",
        body: [
          "International delivery is not yet available through online checkout. Contact our care team and we will confirm whether we can ship to your destination.",
        ],
      },
    ],
  },
  returns: {
    slug: "returns",
    path: "/returns",
    eyebrow: "Customer care",
    title: "Returns & order issues",
    intro: `Unopened pantry goods may be returned within ${returns.windowDays} days of delivery.`,
    sections: [
      {
        id: "return-policy",
        title: "Starting a return",
        body: [
          `Email ${contact.email} with your order number and the items you would like to return. We will reply ${support.responseTime} with instructions.`,
          `A ${formatCurrency(returns.returnShippingFee)} return-label fee is deducted from the refund unless the item arrived damaged or incorrect. Original shipping fees are not refundable.`,
        ],
      },
      {
        id: "food-safety",
        title: "Food safety",
        body: [
          "We cannot accept an opened food product back into inventory. If the flavor or condition is not what you expected, please contact us so we can make it right.",
        ],
        highlights: returns.nonReturnable,
      },
      {
        id: "damage",
        title: "Damaged or incorrect orders",
        body: [
          "Send a photo of the shipping box and affected item within seven days of delivery. We will arrange a replacement or refund after reviewing the claim.",
        ],
      },
    ],
  },
  faq: {
    slug: "faq",
    path: "/faq",
    eyebrow: "Good to know",
    title: "Frequently asked questions",
    intro: "Straight answers about freshness, sourcing, storage, allergens, and orders.",
    groups: [
      {
        title: "Spices & sourcing",
        items: [
          {
            question: "How fresh are the spices?",
            answer:
              "We purchase focused lots and grind or blend in small batches. Whole spices generally hold their aroma longer than ground spices, so each product includes storage guidance and a practical best-use window.",
          },
          {
            question: "Does single origin mean one farm?",
            answer:
              "Not always. We use single origin to mean one clearly identified growing region. When a lot is traceable to one farm or cooperative, we say so on the product page.",
          },
          {
            question: "Are your blends salted?",
            answer:
              "Most are salt-free. Any salt, sesame, or other ingredient is listed in full in the product attributes, so you can season with confidence.",
          },
        ],
      },
      {
        title: "Storage & allergens",
        items: [
          {
            question: "Where should I keep my spices?",
            answer:
              "Keep jars tightly sealed in a cool, dry cupboard away from the stove and direct light. Refrigeration can introduce moisture and is not recommended for most spices.",
          },
          {
            question: "Are the products gluten-free?",
            answer:
              "Products labeled gluten-free contain no gluten ingredients. Our pantry also handles sesame and mustard, so customers with severe allergies should review each ingredient list and contact us before ordering.",
          },
          {
            question: "Can I refill the glass jars?",
            answer:
              "Yes. Wash and dry the jar completely, then transfer the matching refill pouch. Avoid topping a new batch over older spices so the freshest aroma is preserved.",
          },
        ],
      },
      {
        title: "Orders",
        items: [
          {
            question: "Can I change an order after checkout?",
            answer: `Contact ${contact.email} as soon as possible. We can usually help before packing begins, but changes are not guaranteed once an order enters fulfillment.`,
          },
          {
            question: "Do you offer gift notes?",
            answer:
              "Yes. Add a short note at checkout and we will print it on an unpriced card. Gift wrapping and scheduled delivery are not currently available.",
          },
        ],
      },
    ],
  },
  contact: {
    slug: "contact",
    path: "/contact",
    eyebrow: "Let’s talk spice",
    title: "Contact us",
    intro: `Questions about an order, ingredient, or recipe? The ${brandName} care team is happy to help.`,
    details: [
      { label: "Email", value: contact.email, href: `mailto:${contact.email}` },
      { label: "Hours", value: contact.hours },
      { label: "Pantry", value: contact.address.formatted },
    ],
    responseNote: `We usually reply ${support.responseTime}. Please include your order number when writing about a purchase.`,
    subjects: ["Order help", "Product question", "Wholesale", "Press", "Other"],
  },
  privacy: {
    slug: "privacy",
    path: "/privacy",
    eyebrow: "Legal",
    title: "Privacy policy",
    intro: `This policy explains how ${brandName} handles information shared through our storefront.`,
    updatedAt: "September 17, 2026",
    sections: [
      {
        id: "information",
        title: "Information we collect",
        body: [
          "We may collect contact, delivery, account, order, device, and storefront-usage information when you browse, place an order, or contact support.",
          "Payment details should be handled by the payment provider embedded at checkout; we do not intend to store complete payment-card numbers in our application database.",
        ],
      },
      {
        id: "use",
        title: "How information is used",
        body: [
          "We use information to fulfill orders, provide support, prevent fraud, improve the storefront, and meet legal obligations.",
        ],
      },
      {
        id: "cookies-tracking",
        title: "Cookies and similar technologies",
        body: [
          "We use essential browser storage to keep the storefront secure and remember cart choices. Optional analytics technologies run only where applicable consent has been obtained. See our Cookie Policy for details.",
        ],
      },
      {
        id: "sharing",
        title: "When information is shared",
        body: [
          "We may share only the information needed with service providers that support payments, fulfillment, hosting, fraud prevention, analytics, and customer care. We do not sell personal information for money.",
        ],
      },
      {
        id: "retention-security",
        title: "Retention and security",
        body: [
          "We retain information only as long as reasonably needed for the purposes described here, including legal, accounting, dispute, and fraud-prevention obligations. We use administrative and technical safeguards, though no online system can be guaranteed completely secure.",
        ],
      },
      {
        id: "choices",
        title: "Your choices",
        body: [
          `For access, correction, deletion, restriction, portability, or objection requests, email ${contact.email}. Some records may be retained where law or fraud prevention requires it.`,
        ],
      },
    ],
  },
  cookies: {
    slug: "cookies",
    path: "/cookies",
    eyebrow: "Legal",
    title: "Cookie policy",
    intro: `This policy explains how ${brandName} uses cookies and browser storage on this storefront.`,
    updatedAt: "September 17, 2026",
    sections: [
      { id: "what", title: "What cookies are", body: ["Cookies and similar technologies are small pieces of data stored or read by a website to provide features, remember choices, measure performance, and help protect accounts and transactions."] },
      { id: "essential", title: "Essential storage", body: ["Essential storage supports navigation, cart and wishlist state, checkout continuity, security, and accessibility preferences. Because the storefront cannot operate correctly without it, this storage is active when you use those features."] },
      { id: "optional", title: "Optional technologies", body: ["Analytics, personalization, or advertising technologies should be disabled by default where consent is required. If introduced, the consent control will identify their purposes and let you accept or reject non-essential categories."] },
      { id: "manage", title: "Managing your choices", body: ["You can delete or block cookies through your browser settings. Blocking essential storage may prevent the cart, account, or checkout from working as expected. You may also revisit any cookie-preference control made available on the storefront."] },
      { id: "contact", title: "Questions", body: [`Questions about cookies or privacy can be sent to ${contact.email}.`] },
    ],
  },
  terms: {
    slug: "terms",
    path: "/terms",
    eyebrow: "Legal",
    title: "Terms of use",
    intro: `These terms apply when you browse or purchase from the ${brandName} storefront.`,
    updatedAt: "August 11, 2026",
    sections: [
      {
        id: "storefront",
        title: "Storefront information",
        body: [
          "We work to keep product descriptions, availability, pricing, and imagery accurate. We may correct errors, limit quantities, or cancel an order when information is materially incorrect.",
        ],
      },
      {
        id: "orders",
        title: "Orders and payment",
        body: [
          `Displayed prices use ${siteConfig.currency.code}. An order is accepted when we send confirmation of fulfillment, not merely when checkout is submitted. Applicable tax and delivery charges are shown before payment.`,
        ],
      },
      {
        id: "food-guidance",
        title: "Food and recipe guidance",
        body: [
          "Ingredient notes and recipes are provided for general culinary information. Customers are responsible for reviewing ingredient and allergen details and deciding whether a product is suitable for them.",
        ],
      },
      {
        id: "contact",
        title: "Questions",
        body: [`Questions about these terms can be sent to ${contact.email}.`],
      },
    ],
  },
};

export const getContentPage = (slug) => contentPages[slug] || null;
export const contentPageList = Object.values(contentPages);

export { contentPages };
export default contentPages;
